import { Button, CustomDownIcon as CustomDown, Menu, MenuDivider, MenuItem, MenuList } from '@lokalise/louis';
import type { FC } from 'react';
import { useState } from 'react';

import { scheduleToast } from '@app/components/Toast';
import commentsActions from '@app/features/comments/store/actions';
import useDispatch from '@app/hooks/useDispatch';
import type { RootState } from '@app/types';
import { navigateToSearch } from '@app/utils/location';

import BranchCreateModal from '../BranchCreateModal';
import { BranchNameText, Container } from './styles';

type BranchSelectProps = {
    projectId: string;
    currentBranchName: string;
    hasAdminRightsSettings: boolean;
    hasAdminRightsCreateBranch: boolean;
    branches: RootState['branch'];
    view: string | null;
    search: string | null;
};

const BranchSelect: FC<BranchSelectProps> = ({
    projectId,
    currentBranchName,
    hasAdminRightsSettings,
    hasAdminRightsCreateBranch,
    branches,
    view,
    search,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const dispatch = useDispatch();

    const navigateToBranch = (branchName: string) => {
        // Comments sidebar has to be closed before switching branches.
        // Otherwise, it will try to load comments for key from different branch.
        dispatch(commentsActions.closeSidebar());

        const branchUrl = `?view=${view || ''}&branch=${branchName || ''}&search=${search || ''}`;
        navigateToSearch(branchUrl);
    };

    return branches.branchToggle ? (
        <>
            <Menu
                menuButton={() => (
                    <Button size="xs" variant="primary" rightIcon={<CustomDown />}>
                        <BranchNameText>{branches.branchName || 'master'}</BranchNameText>
                    </Button>
                )}
            >
                <MenuList>
                    <Container>
                        <MenuItem onClick={() => navigateToBranch('master')} selected={currentBranchName === 'master'}>
                            master
                        </MenuItem>
                        {branches.list.map(({ id, name }) => (
                            <MenuItem
                                key={id}
                                onClick={() => navigateToBranch(name)}
                                selected={currentBranchName === name}
                            >
                                {name}
                            </MenuItem>
                        ))}
                        {(hasAdminRightsCreateBranch || hasAdminRightsSettings) && <MenuDivider />}
                        {hasAdminRightsCreateBranch && (
                            <MenuItem aria-label="Create Branch" onClick={() => setModalOpen(true)}>
                                Create branch...
                            </MenuItem>
                        )}
                        {hasAdminRightsSettings && (
                            <MenuItem aria-label="Branches" href={`/settings/${projectId}/#branches`}>
                                Branches
                            </MenuItem>
                        )}
                    </Container>
                </MenuList>
            </Menu>
            {modalOpen && (
                <BranchCreateModal
                    projectId={projectId}
                    opened={modalOpen}
                    onClose={() => setModalOpen(false)}
                    onBranchCreated={(branchName) => {
                        dispatch(commentsActions.closeSidebar());

                        scheduleToast({
                            type: 'success',
                            title: `Branch ${branchName} successfully created.`,
                        });

                        window.location.href = `${
                            window.location.href.split('?')[0]
                        }?view=&branch=${branchName}&search=`;
                    }}
                />
            )}
        </>
    ) : (
        <Button size="xs" disabled>
            All branches
        </Button>
    );
};

export default BranchSelect;
